import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthSession } from '@/features/auth/useAuthSession';
import { usePlatform } from '@/platform/usePlatform';
import { createCheckout, PaymentsError } from '@/services/payments';

export type PurchaseStatus = 'idle' | 'opening' | 'waiting' | 'error' | 'cancelled';

/**
 * Через сколько миллисекунд перечитать баланс после оплаты. Кредиты
 * начисляет сервер по вебхуку Telegram — обычно за секунды; клиент только
 * переспрашивает, сам баланс не меняет (инвариант 5).
 */
const BALANCE_REFRESH_DELAYS_MS = [1_000, 3_000, 6_000, 12_000, 20_000];

const WAITING =
  'Оплата получена. Кредиты появятся через несколько секунд. Если через минуту их нет, напишите в поддержку, мы проверим платёж.';

export function usePurchase() {
  const platform = usePlatform();
  const { session } = useAuthSession();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<PurchaseStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
    },
    [],
  );

  const buy = useCallback(
    (packId: string) => {
      const token = session?.access_token;

      if (!token) {
        setStatus('error');
        setMessage('Нет активной сессии. Откройте приложение снова.');
        return;
      }

      setStatus('opening');
      setMessage(null);

      void (async () => {
        try {
          const checkout = await createCheckout(token, packId);
          const result = await platform.startPayment(checkout);

          if (result === 'paid' || result === 'pending') {
            setStatus('waiting');
            setMessage(WAITING);
            timers.current = BALANCE_REFRESH_DELAYS_MS.map((delay) =>
              setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['credits'] }), delay),
            );
          } else if (result === 'cancelled') {
            setStatus('cancelled');
          } else if (result === 'unsupported') {
            setStatus('error');
            setMessage('Оплата доступна в Студии в Telegram. Откройте её там.');
          } else {
            setStatus('error');
            setMessage('Платёж не прошёл. Попробуйте ещё раз или выберите другой пакет.');
          }
        } catch (error) {
          setStatus('error');
          setMessage(error instanceof PaymentsError ? error.message : 'Не удалось открыть оплату. Попробуйте ещё раз.');
        }
      })();
    },
    [platform, queryClient, session?.access_token],
  );

  return { buy, status, message };
}
